// Survey.jsx – React 19

import {
  useState, useEffect, useCallback, useMemo, useRef, memo,
  use, useActionState, Suspense, Component,
} from 'react';
import { nanoid } from 'nanoid';
import {
  Box, TextField, Button, Typography, Paper, Switch, Link, IconButton, Card, CardContent,
  Container, Tooltip, InputAdornment, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, CircularProgress, Backdrop, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, RadioGroup, Radio, FormLabel, Chip,
  Snackbar, Alert, GlobalStyles, Slide,
} from '@mui/material';
import ShareIcon              from '@mui/icons-material/Share';
import ContentCopyIcon        from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon              from '@mui/icons-material/Close';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import CommentIcon            from '@mui/icons-material/Comment';
import ImageIcon              from '@mui/icons-material/Image';
import PollIcon               from '@mui/icons-material/Poll';
import EditIcon               from '@mui/icons-material/Edit';
import RefreshIcon            from '@mui/icons-material/Refresh';
import { db } from '../../../config/firebase';
import {
  addDoc, doc, updateDoc, serverTimestamp, getDoc, collection
} from 'firebase/firestore';
import SurveyPreview         from '../../../cliente/SurveyPreview';
import SmartphonePreviewWrapper from '../../../cliente/SmartphonePreviewWrapper';
import { useAuth }           from '../../../context/AuthContext';
import { translations }      from './surveyTranslations';
import './ui/backgroundStyles.css';

import SurveyProgressBar   from './ui/SurveyProgressBar';
import FloatingActionButtons from './ui/FloatingActionButtons';
import SurveySettingsModal   from './ui/SurveySettingsModal';
import ConfirmDialog         from './ui/ConfirmDialog';
import QuestionModal         from './ui/QuestionModal';
import { createPromiseCache } from './cacheUtils';

import {
  INITIAL_QUESTION,
  EMOJI_TYPES,
  getNextQuestionId,
  reorderQuestions,
  QuestionsSection,
} from './ui/Questions';

// ====== CONSTANTES ======
const NANO_ID_LENGTH = 14;
export { INITIAL_QUESTION };
export const COPY_TIMEOUT   = 2000;
export const INITIAL_ERRORS = { title: '', description: '', questions: {} };

export { EMOJI_TYPES, getNextQuestionId, reorderQuestions };

const TITLE_MAX_LENGTH = 100;
const DESC_MAX_LENGTH  = 300;

// ── Idioma por defecto ──────────────────────────────────────────────────
// Reglas: si el usuario/cuenta tiene un idioma explícito ('es' | 'en') en
// useAuth(), se respeta siempre. Si NO hay preferencia explícita (usuario
// anónimo, cuenta sin configurar, etc.), se usa inglés por defecto, EXCEPTO
// si el navegador reporta español, en cuyo caso se usa español.
const detectBrowserLang = () => {
  if (typeof navigator === 'undefined') return 'en';
  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || navigator.userLanguage || ''];
  const isSpanish = candidates.some((l) => (l || '').toLowerCase().startsWith('es'));
  return isSpanish ? 'es' : 'en';
};

// ── Resultado final: reglas de interpretación ──────────────────────────
// Cada regla evalúa "suma_global {operator} value" → si es verdadera, se
// usa esa interpretación (label). Operadores soportados: '=', '<', '>',
// 'between' (rango inclusivo entre value y valueTo, en cualquier orden).
export const RESULT_OPERATORS = ['=', '<', '>', 'between'];
const RESULT_LABEL_MAX_LENGTH   = 60;
const RESULT_DETAILS_MAX_LENGTH = 300;

// Reglas por defecto: 1 a 5 con igualdad exacta, igual escala que los
// emojis. label vacío por defecto → se muestra el número tal cual.
// valueTo solo se usa cuando operator === 'between'.
// detalles: texto largo opcional con información adicional del resultado.
const createDefaultResultRules = () =>
  [1, 2, 3, 4, 5].map((value) => ({
    id: nanoid(6),
    operator: '=',
    value,
    valueTo: '',
    label: '',
    detalles: '',
  }));

// Evalúa una regla de resultado contra la suma global de respuestas.
// Usar esta función (en vez de comparar manualmente) en cualquier lugar
// donde se calcule/muestre el resultado final (p. ej. Resultado.jsx),
// para que soporte correctamente el operador 'between'.
export const evaluateResultRule = (rule, sum) => {
  const value   = Number(rule.value);
  const valueTo = Number(rule.valueTo);
  switch (rule.operator) {
    case '=': return sum === value;
    case '<': return sum < value;
    case '>': return sum > value;
    case 'between': {
      if (rule.valueTo === '' || rule.valueTo === undefined || rule.valueTo === null) return false;
      const min = Math.min(value, valueTo);
      const max = Math.max(value, valueTo);
      return sum >= min && sum <= max;
    }
    default:
      return false;
  }
};

// ====== GLOBAL STYLES ======
const SURVEY_GLOBAL_STYLES = {
  '@keyframes questionSlideIn': {
    from: { opacity: 0, transform: 'translateY(-10px) scale(0.98)' },
    to:   { opacity: 1, transform: 'translateY(0)   scale(1)' },
  },
  '@keyframes unsavedPulse': {
    '0%, 100%': { opacity: 1 },
    '50%':      { opacity: 0.5 },
  },
  '@keyframes shimmer': {
    '0%':   { backgroundPosition: '-200% center' },
    '100%': { backgroundPosition:  '200% center' },
  },
  '@keyframes floatIcon': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%':      { transform: 'translateY(-4px)' },
  },
};

// ====== HELPERS ======
export const createInitialSurvey = (initialData, userId, subscriptionActive = false) => ({
  title:       initialData?.title       || '',
  description: initialData?.description || '',
  isActive:    true,
  urlId:       initialData?.urlId       || nanoid(NANO_ID_LENGTH),
  createdBy:   initialData?.createdBy   || userId,
  questions:   initialData?.questions   || { q1: { ...INITIAL_QUESTION } },
  showLogo:    initialData?.showLogo    ?? subscriptionActive,
  showComment: initialData?.showComment ?? false,
  // Mostrar resultado final al usuario. Default SIEMPRE false — es opt-in.
  showResult:  initialData?.showResult  ?? false,
  // Reglas de interpretación del resultado final:
  // [{ id, operator, value, valueTo, label, detalles }]
  // Por defecto: 5 reglas de igualdad (1 a 5) con label vacío (se muestra
  // el número tal cual si no se personaliza).
  resultRules: initialData?.resultRules || createDefaultResultRules(),
  style:       initialData?.style       || 'bg-white',
  modoPro:     initialData?.modoPro     ?? subscriptionActive,
});

export const createSurvey = async (surveyData) => {
  try {
    const newSurvey = {
      ...surveyData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive:  true,
      isDelete:  false,
      urlId:     surveyData?.urlId || nanoid(NANO_ID_LENGTH),
    };
    const docRef = await addDoc(collection(db, 'surveys'), newSurvey);
    return { id: docRef.id, ...newSurvey };
  } catch (error) {
    console.error('Error creating survey:', error);
    throw error;
  }
};

export const updateSurvey = async (surveyId, surveyData) => {
  try {
    const surveyRef = doc(db, 'surveys', surveyId);
    await updateDoc(surveyRef, { ...surveyData, updatedAt: serverTimestamp() });
    return { id: surveyId, ...surveyData };
  } catch (error) {
    console.error('Error updating survey:', error);
    throw error;
  }
};

// ====== SHARED UI COMPONENTS =============================================

export const SurveyLoader = ({ t }) => (
  <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
    <CircularProgress />
    <Typography variant="h6" sx={{ mt: 2 }}>{t.loading}</Typography>
  </Container>
);

export class SurveyErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }

  handleRetry = () => {
    this.props.onRetry?.();
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      const { lang, errorMessage } = this.props;
      return (
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
              >
                {lang === 'es' ? 'Reintentar' : 'Retry'}
              </Button>
            }
          >
            {errorMessage ?? (lang === 'es' ? 'Error al cargar.' : 'Failed to load.')}
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}

const logoCache = createPromiseCache();

// FIX: promesa constante en vez de crear una instancia nueva de Promise.resolve()
// en cada render de LogoImage cuando no hay logo/suscripción activa.
const DEFAULT_LOGO_PROMISE = Promise.resolve('/logop.png');

function getLogoPromise(uid, subscriptionActive) {
  if (!uid || !subscriptionActive) return DEFAULT_LOGO_PROMISE;
  return logoCache.getOrCreate(uid, () =>
    getDoc(doc(db, 'Logo', uid))
      .then((snap) => (snap.exists() ? snap.data().Url || '/logop.png' : '/logop.png'))
      .catch(() => '/logop.png'),
  );
}

const LogoImage = memo(({ uid, subscriptionActive }) => {
  const logoUrl = use(getLogoPromise(uid, subscriptionActive));
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <img src={logoUrl} alt="Logo" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
    </Box>
  );
});
LogoImage.displayName = 'LogoImage';

// ====== SHARE URL =======================================================
const ShareUrlSection = memo(({ shareUrl, copied, onCopyUrl, t }) => (
  <Box sx={{ mt: 4 }}>
    <Card variant="outlined" sx={{ p: 3, background: 'rgba(25,118,210,0.05)', borderColor: 'primary.light', borderRadius: 2, borderWidth: '2px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ShareIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>{t.shareLinkTitle}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          size="medium" value={shareUrl} variant="outlined"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? t.copiedTooltip : t.copyTooltip}>
                  <IconButton edge="end" onClick={onCopyUrl} color={copied ? 'success' : 'primary'} sx={{ transition: 'color 0.2s' }}>
                    {copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <Link href={shareUrl} target="_blank" underline="none">
          <Button variant="contained" size="medium" startIcon={<ShareIcon />}
            sx={{ minWidth: '140px', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            {t.viewSurveyButton}
          </Button>
        </Link>
      </Box>
    </Card>
  </Box>
));
ShareUrlSection.displayName = 'ShareUrlSection';

// ====== DIALOGS =========================================================
const PreviewDialog = memo(({ open, onClose, survey, t }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle>{t.previewTitle}</DialogTitle>
    <DialogContent dividers><SmartphonePreviewWrapper survey={survey} /></DialogContent>
    <DialogActions><Button onClick={onClose} color="primary">{t.closeButton}</Button></DialogActions>
  </Dialog>
));
PreviewDialog.displayName = 'PreviewDialog';

const LimitDialog = memo(({ open, onClose, title, message, closeLabel, upgradeLabel, onUpgrade }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="secondary">{closeLabel}</Button>
      <Button onClick={onUpgrade} color="primary" variant="contained">{upgradeLabel}</Button>
    </DialogActions>
  </Dialog>
));
LimitDialog.displayName = 'LimitDialog';

// ====== GENERAL INFO CARD ===============================================
const GeneralInfoCard = memo(({
  survey, errors, setSurvey, handleChange, handleStyleChange, shareUrl, copied, onCopyUrl,
  clearError, markDirty, t, subscriptionActive, uid, hasUnsavedChanges, isRecentlySaved,
  settingsOpen, onOpenSettings, logoKey,
  onResultRuleChange, onAddResultRule, onRemoveResultRule,
}) => {
  const [localTitle, setLocalTitle]             = useState(survey.title);
  const [localDescription, setLocalDescription] = useState(survey.description);

  useEffect(() => { setLocalTitle(survey.title); },             [survey.title]);
  useEffect(() => { setLocalDescription(survey.description); }, [survey.description]);

  const syncTitle = useCallback(() => {
    setSurvey((prev) => ({ ...prev, title: localTitle }));
    if (errors.title && localTitle.trim()) clearError('title');
    markDirty();
  }, [localTitle, errors.title, clearError, setSurvey, markDirty]);

  const syncDescription = useCallback(() => {
    setSurvey((prev) => ({ ...prev, description: localDescription }));
    markDirty();
  }, [localDescription, setSurvey, markDirty]);

  // FIX: sin memo, `() => onOpenSettings(false)` se recreaba en cada render
  // de GeneralInfoCard, invalidando cualquier memo() interno de
  // SurveySettingsModal que dependa de la identidad de onClose.
  const handleCloseSettings = useCallback(() => onOpenSettings(false), [onOpenSettings]);

  const titleCharsLeft = TITLE_MAX_LENGTH - localTitle.length;

  return (
    <>
      <SurveySettingsModal
        open={settingsOpen}
        onClose={handleCloseSettings}
        survey={survey}
        handleChange={handleChange}
        handleStyleChange={handleStyleChange}
        subscriptionActive={subscriptionActive}
        onResultRuleChange={onResultRuleChange}
        onAddResultRule={onAddResultRule}
        onRemoveResultRule={onRemoveResultRule}
        t={t}
      />
      <Card sx={{ mb: 4, border: '1px solid #e3f2fd', borderRadius: 3, boxShadow: '0 6px 12px rgba(0,0,0,0.08)', background: 'transparent' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
            {survey.showLogo    && <Chip size="small" icon={<ImageIcon />}   label={t.chipLogo}     color="primary"   variant="outlined" />}
            {survey.showComment && <Chip size="small" icon={<CommentIcon />} label={t.chipComments} color="secondary" variant="outlined" />}
            {survey.showResult  && <Chip size="small" icon={<PollIcon />}    label={t.chipResult}   color="info"      variant="outlined" />}
            {hasUnsavedChanges && (
              <Chip size="small" icon={<EditIcon />} label={t.chipUnsaved} color="warning" variant="outlined"
                sx={{ animation: 'unsavedPulse 2s ease-in-out infinite' }} />
            )}
            {isRecentlySaved && !hasUnsavedChanges && (
              <Chip size="small" icon={<CheckCircleOutlineIcon />} label={t.chipSaved} color="success" variant="outlined" />
            )}
          </Box>

          {survey.showLogo && (
            <Suspense fallback={<Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
              <LogoImage key={logoKey} uid={uid} subscriptionActive={subscriptionActive} />
            </Suspense>
          )}

          <TextField
            fullWidth label={t.surveyTitleLabel} value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
            onBlur={syncTitle} error={!!errors.title}
            helperText={errors.title || `${localTitle.length}/${TITLE_MAX_LENGTH}`}
            required variant="outlined" multiline maxRows={3} sx={{ mb: 3 }}
            InputProps={{ sx: { fontSize: '1.1rem', fontWeight: 500, minHeight: '50px', borderRadius: 2 } }}
            FormHelperTextProps={{ sx: { textAlign: errors.title ? 'left' : 'right', color: titleCharsLeft <= 10 && !errors.title ? 'warning.main' : undefined } }}
          />
          <TextField
            fullWidth label={t.surveyDescriptionLabel} value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value.slice(0, DESC_MAX_LENGTH))}
            onBlur={syncDescription} multiline rows={4} variant="outlined"
            helperText={`${localDescription.length}/${DESC_MAX_LENGTH}`}
            FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          {shareUrl && <ShareUrlSection shareUrl={shareUrl} copied={copied} onCopyUrl={onCopyUrl} t={t} />}
        </CardContent>
      </Card>
    </>
  );
});
GeneralInfoCard.displayName = 'GeneralInfoCard';

// ====== COMMENT PREVIEW =================================================
const CommentPreview = memo(({ t }) => (
  <Card elevation={1} sx={{ mt: 2, mb: 2, border: '1px dashed', borderColor: 'secondary.light', borderRadius: 2, background: 'transparent' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CommentIcon fontSize="small" color="secondary" />
        <Typography variant="caption" color="secondary" fontWeight={700} letterSpacing={0.5} textTransform="uppercase">
          {t.commentPreviewLabel}
        </Typography>
        <Chip label={t.commentAtEnd} size="small" color="secondary" variant="outlined" sx={{ ml: 'auto', fontSize: '0.7rem' }} />
      </Box>
      <TextField fullWidth multiline rows={3} disabled placeholder={t.commentPlaceholder}
        label={t.commentFieldLabel} variant="outlined"
        sx={{ pointerEvents: 'none', '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderStyle: 'dashed', borderColor: 'secondary.light' } }, '& .Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.45) !important' } }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
        {t.commentHelperText}
      </Typography>
    </CardContent>
  </Card>
));
CommentPreview.displayName = 'CommentPreview';

// ====== COMPONENTE PRINCIPAL =============================================
export const SharedSurveyForm = ({
  initialSurvey,
  isEditing,
  currentUser,
  onSubmit,
  onCreateNew,
  onDataChange,
  currentSurveyCount = null,
  maxSurveys: maxSurveysProp = null,
}) => {
  const { language, isActive: subscriptionActive, planLimits } = useAuth();

  const [browserLang] = useState(detectBrowserLang);
  const lang = useMemo(() => {
    if (language === 'es' || language === 'en') return language;
    return browserLang;
  }, [language, browserLang]);

  const t    = useMemo(() => translations[lang], [lang]);

  const copyTimeoutRef = useRef(null);
  const inputRefs      = useRef({});
  const savedSurveyRef = useRef(null);
  const previewDataRef = useRef(null);

  const [survey, setSurvey]     = useState(initialSurvey);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied]     = useState(false);
  const [errors, setErrors]     = useState(() => t.initialErrors);
  const [confirmDialogOpen, setConfirmDialogOpen]         = useState(false);
  const [isPreviewOpen, setIsPreviewOpen]                 = useState(false);
  const [isRecentlySaved, setIsRecentlySaved]             = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges]         = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen]     = useState(false);
  const [activeLimitDialog, setActiveLimitDialog]         = useState(null);
  const [surveyLimitCount, setSurveyLimitCount]           = useState(0);
  const [lastAddedQuestionId, setLastAddedQuestionId]     = useState(null);
  const [settingsOpen, setSettingsOpen]                   = useState(false);
  const [validationFailed, setValidationFailed]           = useState(false);
  const [logoKey, setLogoKey]                             = useState(0);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);
  const showSnackbar  = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const maxQuestions = planLimits?.maxQuestions ?? 5;
  const maxSurveys   = maxSurveysProp ?? planLimits?.maxSurveys ?? 5;

  const questionCount       = useMemo(() => Object.keys(survey.questions || {}).length, [survey.questions]);
  const canAddMoreQuestions = questionCount < maxQuestions;

  useEffect(() => {
    if (!currentUser?.uid) return;
    logoCache.delete(currentUser.uid);
    setLogoKey((k) => k + 1);
  }, [currentUser?.uid]);

  const collectFormData = useCallback(() => {
    const collected = {};
    Object.entries(survey.questions).forEach(([qId, question]) => {
      const qText = inputRefs.current[`question_text_${qId}`]?.value?.trim() || question.text || '';
      let extra = {};
      if (question.type === 'radio') {
        extra.options = (question.options || []).map((opt) => ({
          id:   opt.id,
          text: inputRefs.current[`option_text_${qId}_${opt.id}`]?.value?.trim() || opt.text || '',
        }));
      } else if (EMOJI_TYPES.has(question.type)) {
        const leftRef  = inputRefs.current[`config_left_${qId}`];
        const rightRef = inputRefs.current[`config_right_${qId}`];
        extra.config = {
          ...question.config,
          ...(leftRef  && { left:  leftRef.value.trim()  || question.config?.left  || t.defaultEmojiConfig.left  }),
          ...(rightRef && { right: rightRef.value.trim() || question.config?.right || t.defaultEmojiConfig.right }),
        };
      }
      collected[qId] = { ...question, text: qText, ...extra };
    });
    return { ...survey, questions: collected };
  }, [survey, t]);

  const validateForm = useCallback(
    (showErrors = true) => {
      const data      = collectFormData();
      const newErrors = { title: '', description: '', questions: {} };
      let isValid = true, firstErrorField = null;
      if (!data.title.trim()) {
        newErrors.title = t.titleRequired;
        isValid = false;
        firstErrorField = 'title';
      }
      if (!currentUser?.uid) isValid = false;
      Object.entries(data.questions).forEach(([qId, question]) => {
        if (!question.text.trim()) {
          newErrors.questions[qId] = { text: t.questionTextRequired };
          isValid = false;
          if (!firstErrorField) firstErrorField = `question_${qId}`;
        }
        if (question.type === 'radio') {
          if (!question.options || question.options.length < 2) {
            if (!newErrors.questions[qId]) newErrors.questions[qId] = {};
            newErrors.questions[qId].options = t.radioOptionsMinTwo;
            isValid = false;
          } else if (question.options.some((o) => !o.text.trim())) {
            if (!newErrors.questions[qId]) newErrors.questions[qId] = {};
            newErrors.questions[qId].options = t.radioOptionsAllFilled;
            isValid = false;
          }
        }
      });
      if (showErrors) {
        setErrors(newErrors);
        if (!isValid && firstErrorField) {
          setTimeout(() => {
            const el = document.querySelector(`[name="${firstErrorField}"]`) ||
                       document.querySelector(`[data-field="${firstErrorField}"]`);
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
            else window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
      }
      return isValid;
    },
    [collectFormData, currentUser?.uid, t],
  );

  const [saveResult, dispatchSave, isSaving] = useActionState(
    async (_, surveyPayload) => {
      try {
        const saved = await onSubmit(surveyPayload);
        return { type: 'ok', saved };
      } catch (err) {
        return { type: 'err', message: err?.message ?? '' };
      }
    },
    null,
  );

  useEffect(() => {
    if (!saveResult) return;
    if (saveResult.type === 'ok') {
      const { saved } = saveResult;
      savedSurveyRef.current = saved;
      setSurvey((prev) => ({ ...prev, urlId: saved.urlId }));
      setIsRecentlySaved(true);
      setHasUnsavedChanges(false);
      setValidationFailed(false);
      setConfirmDialogOpen(true);
      showSnackbar(isEditing ? t.savedSnackbarEdit : t.savedSnackbarCreate, 'success');
    } else if (saveResult.type === 'limit') {
      setSurveyLimitCount(saveResult.count);
      setActiveLimitDialog('surveys');
    } else {
      showSnackbar(t.saveErrorSnackbar, 'error');
    }
  }, [saveResult, isEditing, showSnackbar, t]);

  const handleSubmitInternal = useCallback(() => {
    const isValid = validateForm(true);
    if (!isValid || !currentUser?.uid) {
      setValidationFailed(true);
      const missing  = [];
      const formData = collectFormData();
      if (!formData.title.trim()) missing.push(t.surveyTitleLabel ?? 'Título');
      Object.values(formData.questions).forEach((q) => {
        if (!q.text?.trim()) missing.push(t.questionTextLabel ?? 'Texto de pregunta');
        if (q.type === 'radio') {
          const opts = q.options || [];
          if (opts.length < 2 || opts.some((o) => !o.text?.trim()))
            missing.push(t.radioOptionsTitle ?? 'Opciones de respuesta');
        }
      });
      const uniqueMissing = [...new Set(missing)];
      showSnackbar(
        uniqueMissing.length > 0
          ? `${t.validationErrorPrefix ?? 'Warning: '} ${uniqueMissing.join(', ')}`
          : t.titleRequired,
        'error',
      );
      return;
    }
    setValidationFailed(false);
    dispatchSave({
      ...collectFormData(),
      createdBy: currentUser.uid,
      urlId: survey.urlId || nanoid(NANO_ID_LENGTH),
      updatedAt: new Date().toISOString(),
      ...(!isEditing && { createdAt: new Date().toISOString() }),
    });
  }, [validateForm, currentUser?.uid, collectFormData, survey.urlId, dispatchSave, isEditing, showSnackbar, t]);

  useEffect(() => {
    if (!hasUnsavedChanges && !isRecentlySaved) setSurvey(initialSurvey);
  }, [initialSurvey, hasUnsavedChanges, isRecentlySaved]);

  useEffect(() => {
    if (survey.urlId && (isEditing || isRecentlySaved))
      setShareUrl(`${window.location.origin}/surveyRes/${survey.urlId}`);
  }, [survey.urlId, isEditing, isRecentlySaved]);

  useEffect(() => {
    if (currentUser?.uid && !survey.createdBy)
      setSurvey((prev) => ({ ...prev, createdBy: currentUser.uid }));
  }, [currentUser?.uid, survey.createdBy]);

  useEffect(() => () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); }, []);

  // FIX: guardamos siempre la última referencia de onDataChange en un ref
  // para que el efecto de debounce dependa únicamente de `survey`. Antes,
  // si el padre no memoizaba onDataChange (caso común con funciones inline),
  // el efecto se reiniciaba en cada render de SharedSurveyForm — no solo
  // cuando `survey` cambiaba — reseteando el timer de 500ms sin necesidad.
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => { onDataChangeRef.current = onDataChange; }, [onDataChange]);

  useEffect(() => {
    if (!onDataChangeRef.current || !survey) return;
    const id = setTimeout(() => onDataChangeRef.current(survey), 500);
    return () => clearTimeout(id);
  }, [survey]);

  useEffect(() => {
    if (!lastAddedQuestionId) return;
    const timer = setTimeout(() => {
      document.querySelector(`[data-question-id="${lastAddedQuestionId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setLastAddedQuestionId(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [lastAddedQuestionId]);

  const isSaveButtonDisabled = useMemo(() => {
    if (isSaving || questionCount === 0) return true;
    if (isEditing) return !hasUnsavedChanges;
    return isRecentlySaved && !hasUnsavedChanges;
  }, [isSaving, questionCount, isEditing, hasUnsavedChanges, isRecentlySaved]);

  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
    setIsRecentlySaved(false);
  }, []);

  // FIX: si no había nada que limpiar (campo ya vacío / questionId sin ese
  // error), devolvemos la misma referencia `prev` para que React haga
  // bail-out del render en vez de crear un objeto (y sub-objetos) nuevos en
  // cada llamada. Antes se llamaba en cada handleChange/handleQuestionChange
  // — es decir, en cada tecleo — generando un setState real aunque errors
  // no cambiara en la práctica.
  const clearError = useCallback((field, questionId = null) => {
    setErrors((prev) => {
      if (questionId) {
        const qError = prev.questions?.[questionId];
        if (!qError || !(field in qError)) return prev;
        const { [field]: _, ...restFields } = qError;
        const nextQuestions = { ...prev.questions };
        if (Object.keys(restFields).length) nextQuestions[questionId] = restFields;
        else delete nextQuestions[questionId];
        const updated  = { ...prev, questions: nextQuestions };
        const noErrors = !updated.title && Object.keys(updated.questions).length === 0;
        if (noErrors) setValidationFailed(false);
        return updated;
      }
      if (!prev[field]) return prev;
      const updated  = { ...prev, [field]: '' };
      const noErrors = !updated.title && Object.keys(updated.questions).length === 0;
      if (noErrors) setValidationFailed(false);
      return updated;
    });
  }, []);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showSnackbar(t.copiedTooltip, 'success');
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => { setCopied(false); copyTimeoutRef.current = null; }, COPY_TIMEOUT);
    } catch {
      showSnackbar(t.saveErrorSnackbar, 'error');
    }
  }, [shareUrl, showSnackbar, t]);

  const handleChange = useCallback(
    (e) => {
      const { name, value, checked } = e.target;
      setSurvey((prev) => ({
        ...prev,
        [name]: name === 'showLogo' || name === 'showComment' || name === 'showResult' ? checked : value,
      }));
      clearError(name);
      markDirty();
    },
    [clearError, markDirty],
  );

  const handleStyleChange = useCallback(
    (arg) => {
      const clase = typeof arg === 'string' ? arg : arg?.clase;
      if (!clase) return;
      setSurvey((prev) => ({ ...prev, style: clase }));
      markDirty();
    },
    [markDirty],
  );

  const handleResultRuleChange = useCallback(
    (id, field, value) => {
      setSurvey((prev) => ({
        ...prev,
        resultRules: (prev.resultRules || []).map((rule) => {
          if (rule.id !== id) return rule;
          if (field === 'label')    return { ...rule, label: value.slice(0, RESULT_LABEL_MAX_LENGTH) };
          if (field === 'detalles') return { ...rule, detalles: value.slice(0, RESULT_DETAILS_MAX_LENGTH) };
          if (field === 'operator') {
            return {
              ...rule,
              operator: value,
              valueTo: value === 'between' ? (rule.valueTo ?? '') : '',
            };
          }
          if (field === 'value')   return { ...rule, value: value === '' ? '' : Number(value) };
          if (field === 'valueTo') return { ...rule, valueTo: value === '' ? '' : Number(value) };
          return rule;
        }),
      }));
      markDirty();
    },
    [markDirty],
  );

  const handleAddResultRule = useCallback(() => {
    setSurvey((prev) => ({
      ...prev,
      resultRules: [
        ...(prev.resultRules || []),
        { id: nanoid(6), operator: '=', value: '', valueTo: '', label: '', detalles: '' },
      ],
    }));
    markDirty();
  }, [markDirty]);

  const handleRemoveResultRule = useCallback(
    (id) => {
      setSurvey((prev) => {
        const rules = prev.resultRules || [];
        if (rules.length <= 1) return prev; // al menos 1 regla
        return { ...prev, resultRules: rules.filter((r) => r.id !== id) };
      });
      markDirty();
    },
    [markDirty],
  );

  const handleQuestionChange = useCallback(
    (qId, field, value) => {
      setSurvey((prev) => ({
        ...prev,
        questions: { ...prev.questions, [qId]: { ...prev.questions[qId], [field]: value } },
      }));
      clearError(field, qId);
      markDirty();
    },
    [clearError, markDirty],
  );

  const handleOpenQuestionModal  = useCallback(() => {
    if (!canAddMoreQuestions) { setActiveLimitDialog('questions'); return; }
    setIsQuestionModalOpen(true);
  }, [canAddMoreQuestions]);

  const handleCloseQuestionModal = useCallback(() => setIsQuestionModalOpen(false), []);

  const handleAddQuestionFromModal = useCallback(
    (payload) => {
      if (!canAddMoreQuestions) { setActiveLimitDialog('questions'); return; }
      let newQuestionId;
      setSurvey((prev) => {
        newQuestionId = getNextQuestionId(prev.questions);
        const count   = Object.keys(prev.questions).length;
        return {
          ...prev,
          questions: {
            ...prev.questions,
            [newQuestionId]: {
              ...INITIAL_QUESTION,
              text:  payload.text,
              type:  payload.type,
              url:   payload.url || '',
              orden: count + 1,
              ...(payload.sliderConfig && { sliderConfig: payload.sliderConfig }),
              ...(payload.config       && { config: payload.config }),
              ...(payload.options      && { options: payload.options }),
            },
          },
        };
      });
      setTimeout(() => setLastAddedQuestionId(newQuestionId), 80);
      markDirty();
      handleCloseQuestionModal();
    },
    [canAddMoreQuestions, handleCloseQuestionModal, markDirty],
  );

  const removeQuestion = useCallback(
    (qId) => {
      setSurvey((prev) => {
        if (Object.keys(prev.questions).length <= 1) return prev;
        const { [qId]: removed, ...rest } = prev.questions;
        return { ...prev, questions: reorderQuestions(rest, removed.orden) };
      });
      setErrors((prev) => {
        const next = { ...prev.questions };
        delete next[qId];
        return { ...prev, questions: next };
      });
      markDirty();
    },
    [markDirty],
  );

  const resetForm = useCallback(() => {
    setSurvey(createInitialSurvey(null, currentUser?.uid, subscriptionActive));
    setErrors(t.initialErrors);
    setIsRecentlySaved(false);
    setHasUnsavedChanges(false);
    setShareUrl('');
    setValidationFailed(false);
    savedSurveyRef.current = null;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentUser?.uid, subscriptionActive, t.initialErrors]);

  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    if (savedSurveyRef.current) { setSurvey(savedSurveyRef.current); savedSurveyRef.current = null; }
  }, []);

  const handleNewSurvey = useCallback(() => {
    setConfirmDialogOpen(false);
    savedSurveyRef.current = null;

    if ((currentSurveyCount ?? 0) >= maxSurveys) {
      setSurveyLimitCount(currentSurveyCount ?? 0);
      setActiveLimitDialog('surveys');
    } else {
      window.location.href = '/platform/createSurvey';
    }
  }, [currentSurveyCount, maxSurveys]);

  const handleOpenPreview = useCallback(() => {
    previewDataRef.current = collectFormData();
    setIsPreviewOpen(true);
  }, [collectFormData]);

  // FIX: antes se pasaba `onClose={() => setIsPreviewOpen(false)}` inline en
  // el JSX — una función nueva en cada render de SharedSurveyForm — lo que
  // invalidaba el memo() de PreviewDialog en cada re-render del padre.
  const handleClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  // FIX: mismo caso que arriba, para el botón de settings del FAB.
  const handleOpenSettingsFab = useCallback(() => setSettingsOpen(true), []);

  const handleUpgradePlan = useCallback(() => { window.location.href = '/platform/suscripciones'; }, []);

  const hasActiveErrors = useMemo(
    () => validationFailed && (!!errors.title || Object.keys(errors.questions || {}).length > 0),
    [validationFailed, errors],
  );

  const closeLimitDialog = useCallback(() => setActiveLimitDialog(null), []);

  return (
    <Container maxWidth="lg" sx={{ py: 4, pb: 10 }}>
      <GlobalStyles styles={SURVEY_GLOBAL_STYLES} />

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            {isEditing ? t.updating : t.saving}
          </Typography>
        </Box>
      </Backdrop>

      <QuestionModal
        open={isQuestionModalOpen}
        onClose={handleCloseQuestionModal}
        onAddQuestion={handleAddQuestionFromModal}
        t={t}
        disableAdd={!canAddMoreQuestions}
        maxQuestions={maxQuestions}
        questionCount={questionCount}
        subscriptionActive={subscriptionActive}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        title={survey.title}
        description={survey.description}
        isEditing={isEditing}
        onNewSurvey={handleNewSurvey}
        shareUrl={shareUrl}
        t={t}
      />

      <PreviewDialog open={isPreviewOpen} onClose={handleClosePreview}
        survey={previewDataRef.current} t={t} />

      <LimitDialog
        open={!!activeLimitDialog}
        onClose={closeLimitDialog}
        title={activeLimitDialog === 'questions' ? t.maxQuestionsReachedTitle : t.maxSurveysReachedTitle}
        message={
          activeLimitDialog === 'questions'
            ? t.maxQuestionsReachedMsg.replace('{max}', maxQuestions).replace('{count}', questionCount)
            : t.maxSurveysReachedMsg.replace('{max}', maxSurveys).replace('{count}', surveyLimitCount)
        }
        closeLabel={t.closeButton}
        upgradeLabel={t.upgradePlan}
        onUpgrade={handleUpgradePlan}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', boxShadow: 4 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box className={survey.style} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, background: 'transparent' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" color="primary"
            sx={{ mb: 4, fontWeight: 'bold', position: 'relative', '&:after': { content: '""', position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: '80px', height: '4px', backgroundColor: 'primary.main', borderRadius: '2px' } }}>
            {isEditing ? t.editSurveyTitle : t.newSurveyTitle}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <GeneralInfoCard
              survey={survey} errors={errors} setSurvey={setSurvey}
              handleChange={handleChange} handleStyleChange={handleStyleChange}
              shareUrl={shareUrl} copied={copied} onCopyUrl={handleCopyUrl}
              clearError={clearError} markDirty={markDirty} t={t}
              subscriptionActive={subscriptionActive} uid={currentUser?.uid}
              hasUnsavedChanges={hasUnsavedChanges} isRecentlySaved={isRecentlySaved}
              settingsOpen={settingsOpen} onOpenSettings={setSettingsOpen} logoKey={logoKey}
              onResultRuleChange={handleResultRuleChange}
              onAddResultRule={handleAddResultRule}
              onRemoveResultRule={handleRemoveResultRule}
            />
            <QuestionsSection
              questions={survey.questions} errors={errors}
              onQuestionChange={handleQuestionChange} onRemoveQuestion={removeQuestion}
              inputRefs={inputRefs} handleMarkChanged={markDirty} clearError={clearError}
              t={t} lastAddedQuestionId={lastAddedQuestionId} subscriptionActive={subscriptionActive}
            />
            {survey.showComment && <CommentPreview t={t} />}
          </Box>
        </Paper>
      </Box>

      <FloatingActionButtons
        onAddQuestion={handleOpenQuestionModal}
        onPreview={handleOpenPreview}
        onSave={handleSubmitInternal}
        onSettings={handleOpenSettingsFab}
        disabledPreview={questionCount === 0 || isSaving}
        disableAdd={!canAddMoreQuestions}
        isSaveDisabled={isSaveButtonDisabled}
        hasValidationError={hasActiveErrors}
        t={t}
      />

      <SurveyProgressBar
        questionCount={questionCount}
        maxQuestions={maxQuestions}
        t={t}
      />
    </Container>
  );
};